import React from "react";
import styled from "styled-components";
import { Icons, getStackIcon } from "@/assets/icons";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../translations/translations";
import { getProjectDetail } from "../../translations/projectDetails";

const ProjectCard = ({
  title,
  description,
  siteLink,
  GhLink,
  path,
  image,
  offline,
  workRepo,
  stack,
  translationKey,
  icons,
  cardIcons,
  isNew,
  dateCreated,
  commitCount,
}) => {
  const { language } = useLanguage();

  // Get translated description if available
  const key = translationKey || path;
  const translatedDescription = getProjectDetail(language, key, "description");
  const displayDescription = translatedDescription || description;

  // Determine which icons to display on the card
  // Priority: cardIcons > icons (first 2) > stack icon
  const iconsToDisplay =
    cardIcons || (icons && icons.length > 0 ? icons.slice(0, 2) : null);
  const singleStackIcon = !iconsToDisplay ? getStackIcon(stack) : null;

  // Format date as dd/mm/yy
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (e) {
      return null;
    }
  };

  const formattedDate = formatDate(dateCreated);

  return (
    <CardBody>
      {isNew && <NewBadge>{getTranslation(language, "common.new")}</NewBadge>}
      <IconContainer>
        {iconsToDisplay ? (
          iconsToDisplay.map((icon, index) => (
            <img key={index} src={icon} alt={`stack-${index}`} />
          ))
        ) : (
          <img src={singleStackIcon} alt="stack" />
        )}
      </IconContainer>
      <CardTitle>
        <div>
          <h2>{title}</h2>
          {offline ? (
            <p>{getTranslation(language, "common.offline")}</p>
          ) : (
            !workRepo && (
              <p style={{ color: "green" }}>
                {getTranslation(language, "common.online")}
              </p>
            )
          )}
          {workRepo ? (
            <p>{getTranslation(language, "common.privateRepo")}</p>
          ) : (
            ""
          )}
        </div>

        <Buttons>
          {offline && (
            <a href={`/projects/${path}`}>
              <img id="website" src={Icons.website} alt={title} />
            </a>
          )}

          {siteLink && !offline && (
            <a href={siteLink} target="_blank" rel="noopener noreferrer">
              <img id="website" src={Icons.website} alt={title} />
            </a>
          )}

          {GhLink && (
            <a href={GhLink} target="_blank" rel="noreferrer">
              <img id="icon" src={Icons.github} alt="Github" />
            </a>
          )}
        </Buttons>
      </CardTitle>
      <Link to={`/projects/${path}`} className="link">
        <img id="image" src={image} alt={title} />
        <CardInfoContainer>
          <CardDesc>
            <p>{displayDescription}</p>
          </CardDesc>
        </CardInfoContainer>
      </Link>
      {(formattedDate || (commitCount !== undefined && commitCount !== null)) && (
        <CardFooter>
          {formattedDate && (
            <CardDate>{formattedDate}</CardDate>
          )}
          {commitCount !== undefined && commitCount !== null && (
            <CardCommits>{commitCount} commits</CardCommits>
          )}
        </CardFooter>
      )}
    </CardBody>
  );
};

export default ProjectCard;

const CardBody = styled.div`
  border: 2px solid rgba(83, 83, 83, 0.3);
  background-color: rgba(200, 233, 250, 0.1);
  width: 25vw;
  min-height: 100%;
  margin: 20px;
  padding: 10px;
  border-radius: 5px;
  position: relative;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;

  @media (max-width: 1200px) {
    width: 30vw;
    margin: 15px;
  }

  @media (max-width: 768px) {
    width: 45vw;
    margin: 10px;
    padding: 8px;
  }

  @media (max-width: 480px) {
    width: 90vw;
    margin: 10px auto;
  }

  .link {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    flex: 1;
  }

  #image {
    margin: 15px 0;
    @media (max-width: 768px) {
      margin: 10px 0;
    }
  }

  &:hover {
    background-color: rgba(204, 243, 255, 0.3);
  }

  img {
    width: 250px;
    height: 150px;
    object-fit: cover;
    margin-right: 10px;
    z-index: 0;
    @media (max-width: 1200px) {
      width: 100%;
      max-width: 250px;
    }
    @media (max-width: 768px) {
      width: 100%;
      max-width: 200px;
      height: 120px;
      margin-right: 0;
    }
    @media (max-width: 480px) {
      max-width: 100%;
      height: 150px;
    }
  }
`;

const IconContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  z-index: 1;

  img {
    width: 25px;
    height: 25px;
    object-fit: contain;

    @media (max-width: 1000px) {
      width: 20px;
      height: 20px;
    }
  }
`;

const Buttons = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  margin: 8px;

  img {
    width: 25px;
    height: 25px;
    transition: all 0.3s;
    margin: 3px 3px;

    @media (max-width: 1000px) {
      width: 20px;
      height: 20px;
    }

    &:hover {
      width: 27px;
      height: 27px;
    }
  }
`;

const CardInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CardTitle = styled.div`
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 5px;

  p {
    font-size: 12px;
    color: red;
    margin: 0;
    @media (max-width: 480px) {
      font-size: 10px;
    }
  }

  h2 {
    max-width: 250px;
    @media (max-width: 768px) {
      max-width: 200px;
      font-size: 1rem;
    }
    @media (max-width: 480px) {
      max-width: 100%;
      font-size: 0.9rem;
    }
  }
`;

const CardDesc = styled.div`
  p {
    text-align: start;
    padding: 0px 10px;
    font-size: 16px !important;
    margin-top: 10px;
    @media (max-width: 768px) {
      font-size: 12px !important;
    }
  }
`;

const NewBadge = styled.div`
  position: absolute;
  top: -15px;
  right: -15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 3;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: pulse 2s ease-in-out infinite;

  @media (max-width: 1000px) {
    font-size: 10px;
    padding: 3px 8px;
    top: -6px;
    right: -6px;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
  border-top: 1px solid rgba(83, 83, 83, 0.2);
  min-height: 30px;

  @media (max-width: 768px) {
    padding: 8px;
    margin-top: 6px;
    min-height: 28px;
  }
`;

const CardDate = styled.div`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 10px;
  }

  @media (max-width: 480px) {
    font-size: 9px;
  }
`;

const CardCommits = styled.div`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 10px;
  }

  @media (max-width: 480px) {
    font-size: 9px;
  }
`;
